import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import {
  saveQuote,
  loadQuotes,
  deleteQuote as storageDeleteQuote,
  saveService,
  loadServices,
  deleteService as storageDeleteService,
} from '../../utils/offlineStorage';
import { Service } from '../api/servicesApi';

export interface OfflineQuote {
  id: string;
  payload: any;
  title: string;
}

export interface OfflineService {
  id: string;
  payload: Partial<Service>;
  name: string;
}

interface OfflineState {
  quotes: OfflineQuote[];
  services: OfflineService[];
  crmMode?: string;
  slackMode?: string;
}

const initialState: OfflineState = { quotes: [], services: [], crmMode: 'native', slackMode: 'native' };

export const initOffline = createAsyncThunk('offline/init', async () => {
  const quotes = (await loadQuotes()) as OfflineQuote[];
  const services = (await loadServices()) as OfflineService[];
  return { quotes, services };
});

const offlineSlice = createSlice({
  name: 'offline',
  initialState,
  reducers: {
    addQuoteState(state, action: PayloadAction<OfflineQuote>) {
      state.quotes.push(action.payload);
    },
    removeQuoteState(state, action: PayloadAction<string>) {
      state.quotes = state.quotes.filter((q) => q.id !== action.payload);
    },
    addServiceState(state, action: PayloadAction<OfflineService>) {
      state.services.push(action.payload);
    },
    removeServiceState(state, action: PayloadAction<string>) {
      state.services = state.services.filter((s) => s.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initOffline.fulfilled, (state, action) => {
      state.quotes = action.payload.quotes;
      state.services = action.payload.services;
    });
  },
});

export const addOfflineQuote = createAsyncThunk(
  'offline/addQuote',
  async (quote: OfflineQuote, { dispatch }) => {
    await saveQuote(quote);
    dispatch(offlineSlice.actions.addQuoteState(quote));
  }
);

export const removeOfflineQuote = createAsyncThunk(
  'offline/removeQuote',
  async (id: string, { dispatch }) => {
    await storageDeleteQuote(id);
    dispatch(offlineSlice.actions.removeQuoteState(id));
  }
);

export const addOfflineService = createAsyncThunk(
  'offline/addService',
  async (service: OfflineService, { dispatch }) => {
    await saveService(service);
    dispatch(offlineSlice.actions.addServiceState(service));
  }
);

export const removeOfflineService = createAsyncThunk(
  'offline/removeService',
  async (id: string, { dispatch }) => {
    await storageDeleteService(id);
    dispatch(offlineSlice.actions.removeServiceState(id));
  }
);

export default offlineSlice.reducer;
export const { addQuoteState, removeQuoteState, addServiceState, removeServiceState } = offlineSlice.actions;
export type { OfflineState };
